/**
 * Service centralisé pour la gestion des erreurs
 */
export class ErrorService {
  private static instance: ErrorService
  private errorHandlers: Map<string, (error: Error) => void> = new Map()

  private constructor() {}

  static getInstance(): ErrorService {
    if (!ErrorService.instance) {
      ErrorService.instance = new ErrorService()
    }
    return ErrorService.instance
  }

  /**
   * Enregistre un gestionnaire d'erreur pour un type spécifique
   */
  registerErrorHandler(type: string, handler: (error: Error) => void): void {
    this.errorHandlers.set(type, handler)
  }

  /**
   * Traite une erreur avec le bon gestionnaire
   */
  handleError(error: Error, type: string = "default", context?: string): void {
    const handler = this.errorHandlers.get(type) || this.defaultErrorHandler

    // Ajouter le contexte à l'erreur si fourni
    if (context) {
      const enhancedError = new Error(`${context}: ${error.message}`)
      enhancedError.stack = error.stack
      handler(enhancedError)
    } else {
      handler(error)
    }
  }

  private defaultErrorHandler(error: Error): void {
    console.error("Erreur non gérée:", error)

    // En production, vous pourriez vouloir envoyer l'erreur à un service de monitoring
    if (import.meta.env.PROD) {
      // Exemple: Sentry, LogRocket, etc.
      // Sentry.captureException(error)
    }
  }

  /**
   * Détermine si une erreur est liée au réseau
   */
  isNetworkError(error: Error): boolean {
    return (
      error.message.includes("fetch") ||
      error.message.includes("network") ||
      error.message.includes("timeout") ||
      error.name === "TypeError"
    )
  }

  /**
   * Détermine si une erreur est liée à l'authentification
   */
  isAuthError(error: Error): boolean {
    return (
      error.message.includes("401") ||
      error.message.includes("403") ||
      error.message.includes("unauthorized")
    )
  }

  /**
   * Crée un message d'erreur user-friendly
   */
  getUserFriendlyMessage(error: Error): string {
    if (this.isNetworkError(error)) {
      return "Problème de connexion réseau. Veuillez vérifier votre connexion et réessayer."
    }

    if (this.isAuthError(error)) {
      return "Problème d'authentification. Veuillez vous reconnecter."
    }

    if (error.message.includes("non trouvé")) {
      return "Les données demandées sont introuvables."
    }

    return "Une erreur inattendue s'est produite. Veuillez réessayer."
  }
}

// Export d'une instance singleton
export const errorService = ErrorService.getInstance()
